import { inject, Injectable, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router, UrlTree } from '@angular/router';

import { firstValueFrom } from 'rxjs';
import { filter, take, timeout } from 'rxjs/operators';

import { ProfileService } from '@iw/core/services';
import { environment } from '@iw/environment';
import { UserStore } from '@iw/users';

import { AuthStore } from './auth.store';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  readonly #store = inject(AuthStore);
  readonly #router = inject(Router);
  readonly #profileService = inject(ProfileService);
  readonly #injector = inject(Injector);

  async signedInCheck(): Promise<true | UrlTree> {
    if (environment.useMocks) {
      this.#store.setAuthStatus(true);
      return true;
    }

    this.#profileService.initializeAuth();

    let userStore: InstanceType<typeof UserStore> | null = null;
    try {
      userStore = inject(UserStore, { optional: true });
    } catch (error) {
      const authorized = this.#store.isAuthorized();
      return authorized ? true : this.#getSignInUrlTree();
    }

    if (!userStore) {
      const authorized = this.#store.isAuthorized();
      return authorized ? true : this.#getSignInUrlTree();
    }

    const isAuthorized$ = toObservable(this.#store.isAuthorized, {
      injector: this.#injector,
    });

    const getMeStatus$ = toObservable((userStore as any).getMeStatus, {
      injector: this.#injector,
    });

    try {
      if (userStore.user() && this.#store.isAuthorized()) {
        return true;
      }

      const currentStatus = (userStore as any).getMeStatus();

      if (currentStatus === 'idle' || currentStatus === 'pending') {
        await firstValueFrom(
          getMeStatus$.pipe(
            filter((status) => status !== 'idle' && status !== 'pending'),
            take(1),
            timeout(5000),
          ),
        );
      }

      await firstValueFrom(
        isAuthorized$.pipe(
          filter(() => {
            const authorized = this.#store.isAuthorized();
            const getMeStatus = (userStore as any).getMeStatus();
            const hasUser = !!userStore.user();

            return (
              authorized ||
              getMeStatus === 'error' ||
              (!hasUser && getMeStatus === 'success')
            );
          }),
          take(1),
          timeout(1000),
        ),
      );
    } catch (error) {
      const authorized = this.#store.isAuthorized();
      return authorized ? true : this.#getSignInUrlTree();
    }

    const authorized = this.#store.isAuthorized();
    return authorized ? true : this.#getSignInUrlTree();
  }

  async signedOutCheck(): Promise<boolean | UrlTree> {
    if (environment.useMocks) {
      this.#store.setAuthStatus(true);
      return this.#router.parseUrl('/dashboard');
    }

    const currentPath = window.location.pathname;
    const isTfaRoute = currentPath.includes('/signin/tfa');

    if (!isTfaRoute) {
      this.#profileService.initializeAuth();
    }

    let userStore: InstanceType<typeof UserStore> | null = null;
    try {
      userStore = inject(UserStore, { optional: true });
    } catch (error) {
      /* empty */
    }

    if (userStore && !userStore.user()) {
      const getMeStatus$ = toObservable((userStore as any).getMeStatus, {
        injector: this.#injector,
      });

      try {
        await firstValueFrom(
          getMeStatus$.pipe(
            filter((status) => status !== 'idle' && status !== 'pending'),
            take(1),
            timeout(5000),
          ),
        );
      } catch (error) {
        /* empty */
      }
    }

    const authorized = this.#store.isAuthorized();

    if (authorized) {
      return this.#router.parseUrl('/dashboard');
    }

    return true;
  }

  logout(): void {
    this.#store.logout();
  }

  clearAuthState(): void {
    this.#store.clearAuthState();
  }

  #getSignInUrlTree(): UrlTree {
    const path =
      window.location.pathname.indexOf('signin') >= 0
        ? ''
        : window.location.pathname;

    return this.#router.parseUrl(
      !path || path === '/'
        ? '/signin'
        : '/signin?url=' + encodeURIComponent(path),
    );
  }
}
